import { Injectable } from '@angular/core';
import { GameAnswerObject } from '../data-models/game-answer-object';
import allOptions from 'src/assets/datas/game_contents.json';
import { CookieService } from './cookie.service';

@Injectable({
  providedIn: 'root'
})
export class QuestionService {

  public difficulty: Difficulty;
  public tutorialSeen = false;

  private unAsked: GameAnswerObject[];
  private questionQueue: QuestionData[];

  private createIndexes = [];
  public passedQuestions = 0;

  constructor(private cookieService: CookieService) { }

  public SetNewGameValues(checkCookies: boolean = false): void{
    this.passedQuestions = 0;
    this.createIndexes = [];
    this.questionQueue = [];
    this.SaveCurrentValues();
  }

  public GetNextQuestion(): QuestionData{

    if (!this.questionQueue?.length) {
      this.InitializeQuestionQueue();
    }

    return this.questionQueue.shift();
  }

  private InitializeQuestionQueue(previousIndexes?: number[]): void {

    if (!previousIndexes) {
      this.createIndexes = [];
    }

    this.unAsked = [];
    let notIncludedCount = 0;
    for (const option of allOptions){

      // Ecologic options don't have a medium difficulty option and should be left out of medium game altogether
      const isMediumEcologic =
        this.difficulty === Difficulty.Medium
        && (option.content.MED.fi === null || (option.content.MED.fi.length === 0));

      // It was later decided that ecologic options should also be dropped from easy mode
      const isEasyEcologic = this.difficulty === Difficulty.Easy && option.content.EASY.fi.includes('Luomu');

      if (!isMediumEcologic && !isEasyEcologic) {
        this.unAsked.push(new GameAnswerObject(option));
      }
      else {
        notIncludedCount++;
      }

    }

    this.questionQueue = [];

    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < (allOptions.length - notIncludedCount); i++) {
      const question = new QuestionData();

      let index;
      if (!previousIndexes) {
        index = this.RandomIndex(this.unAsked);
        this.createIndexes.push(index);
      }
      else {
        index = previousIndexes.shift();
      }

      question.correctAnswer = this.unAsked.splice(index, 1)[0];

      question.wrongAnswers = [];

      for (let j = 0; j < 3; j++){

        let wrongAnswer: GameAnswerObject;

        do {
          if (!previousIndexes) {
            index = this.RandomIndex(allOptions);
            this.createIndexes.push(index);
          }
          else {
            index = previousIndexes.shift();
          }
          wrongAnswer = new GameAnswerObject(allOptions[index]);

        } while (
          question.correctAnswer.Equals(wrongAnswer)
          || !!question.wrongAnswers.find(item => item.Equals(wrongAnswer))
          || (this.difficulty === Difficulty.Medium && (!wrongAnswer.content.MED.fi || wrongAnswer.content.MED.fi.length === 0))
          // ^^ Ecologic options don't have a medium difficulty option and should be left out of medium game altogether
          || (this.difficulty === Difficulty.Easy && wrongAnswer.content.EASY.fi.includes('Luomu'))
          // ^^ Later it was decided that ecologic options should also be left out from easy game
          || wrongAnswer.IsTooSimilar(question.correctAnswer)
          // ^^ Certain options are too similar with eachother. If an wrong answer is too similar to the correct answer, another is chosen.
        );

        question.wrongAnswers.push(wrongAnswer);
      }

      this.questionQueue.push(question);

    }

    this.SaveCurrentValues();

    if (!!previousIndexes) {
      this.questionQueue.splice(0, this.passedQuestions);
    }

  }

  public RandomIndex(array: any[], allowFullLength: boolean = false): number{

    const maxLength = allowFullLength ? array.length : array.length - 1;

    return Math.round(Math.random() * maxLength);
  }

  public CheckRelevantCookieData(): void {

    if (!this.tutorialSeen){
      this.tutorialSeen = this.cookieService.GetCookieValue('tutorialSeen') === 'true';
    }

    if (!this.difficulty) {
      let difficulty = this.cookieService.GetCookieValue('difficulty') as Difficulty;
      if (!difficulty) {
        difficulty = Difficulty.Easy;
      }
      this.difficulty = difficulty;
    }

    if (this.cookieService.GetCookieValue('passedQuestions')) {
      this.passedQuestions = this.cookieService.GetCookieValue('passedQuestions');
    }

    if (!this.questionQueue && !!this.cookieService.GetCookieValue('createIndexes')) {
      const previousIndexes = JSON.parse(this.cookieService.GetCookieValue('createIndexes'));
      this.createIndexes = [];
      try {
        for (const value of previousIndexes) {
          this.createIndexes.push(value);
        }
        this.InitializeQuestionQueue(previousIndexes);
      }
      catch (error) {
        console.log('Faulty recreation data. Creating a new queue');
      }
    }

  }

  public IncrementQuestionCount(): void {
    this.passedQuestions++;
    this.SaveCurrentValues();
  }

  public SaveCurrentValues(): void {
    this.cookieService.SetCookieValue('tutorialSeen', this.tutorialSeen);
    this.cookieService.SetCookieValue('difficulty', this.difficulty);
    this.cookieService.SetCookieValue('passedQuestions', this.passedQuestions);
    this.cookieService.SetCookieValue('createIndexes', JSON.stringify(this.createIndexes));
  }

}

export enum Difficulty {
  UNDEFINED = 'UNDEF',
  Easy = 'EASY',
  Medium = 'MED',
  Hard = 'HARD'
}

export class QuestionData {

  public correctAnswer: GameAnswerObject;
  public wrongAnswers: GameAnswerObject[];

}
